import { getPaymentMode } from '@api/payments';
import PendingVerifications from '@containers/form-verification/form/dialogs/pending-verifications';
import { LeftVerifications } from '@containers/form-verification/form/dialogs/types';
import {
  areMinimumVerificationsComplete,
  onContinueHandlerForPaidForms,
  onContinueHandlerForUnpaidForms
} from '@containers/form-verification/form/helpers';
import styles from '@containers/form-verification/form/styles';
import { StartingProcessDialogType } from '@containers/form-verification/form/types';
import { TxnState } from '@global/constants/verification';
import { trackingEvents } from '@global/monitoring/mixpanel';
import { LoadingButton } from '@mui/lab';
import { Alert, Grid } from '@mui/material';
import { RootState } from '@redux/store';
import { navigateTo } from '@services/navigation';
import mixpanel from 'mixpanel-browser';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ExpressVerificationWarning from './express-verification-warning';
import { ContinueBtn } from './misc';

function FormBottomRow(props: {
  setOpenStartingProcessDialog: React.Dispatch<React.SetStateAction<StartingProcessDialogType>>
}) {
  const { setOpenStartingProcessDialog } = props;
  const formData = useSelector((s: RootState) => s.formData);
  const { txnInfo = {}, txnId, orgInfo } = formData ?? {};

  const [verificationsLeft, setVerificationsLeft] = useState<LeftVerifications>(null);
  const [continueToPay, setContinueToPay] = useState<boolean>(false);

  const [showExpressVerificationWarning, setShowExpressVerificationWarning] =
    React.useState<boolean>(false);
  const [isExpressOpted, setIsExpressOpted] = React.useState<boolean>(false);
  const remainingVerificationsCount = (txnInfo?.filtered?.remaining ?? [])?.length;

  const isPaymentComplete = () => {
    return (
      !txnInfo?.paymentRequired &&
      txnInfo?.userPaymentRequired &&
      txnInfo?.paymentDetails?.length > 0 &&
      TxnState.COMPLETED !== txnInfo?.state
    );
  };

  const willExpressVerificationETABeAffected = () => {
    const filtered = txnInfo?.paymentDetails?.reduce(
      (acc: { express: number, nonExpress: number }, item: any) => {
        if (!item.allVerificationsCompleted) {
          if (item?.expressOpt) {
            acc.express += 1;
          } else {
            acc.nonExpress += 1;
          }
        }
        return acc;
      },
      { express: 0, nonExpress: 0 }
    );
    return Boolean(filtered?.express && filtered?.nonExpress);
  };

  const onPayBtnClick = async () => {
    const minVerificationsComplete = areMinimumVerificationsComplete();
    if (minVerificationsComplete) {
      setVerificationsLeft({
        atleastMapping: minVerificationsComplete
      });
      return null;
    } else {
      if (!txnInfo?.userPaymentRequired) {
        mixpanel.track(trackingEvents.form.events.select.verificationStarted, {
          txnId,
          orgId: orgInfo?.orgId
        });
        return navigateTo('/submitted');
      } else if (txnInfo?.paymentRequired) {
        const res = await getPaymentMode(orgInfo.orgId, txnId);
        if (!res?.error) {
          if (res?.data?.data?.showWarning) {
            setIsExpressOpted(res?.data?.data?.expressAllowed);
            return setShowExpressVerificationWarning(true);
          } else {
            if (res?.data?.data?.expressAllowed) {
              return navigateTo('/select-mode');
            }
            navigateTo('/choose-payment-region');
          }
        }
      } else {
        mixpanel.track(trackingEvents.form.events.select.verificationStarted, {
          txnId,
          orgId: orgInfo?.orgId
        });
        navigateTo('/submitted');
      }
    }
  };

  const showPayBtn = () => {
    if (txnInfo?.userPaymentRequired) {
      return txnInfo?.paymentRequired && txnInfo?.userPaymentRequired;
    } else if (txnInfo?.state === TxnState.COMPLETED) {
      return false;
    }
    return true;
  };

  const onContinueFnBasedOnFormType = (formType: 'paid' | 'unpaid') => {
    if (formType === 'paid') {
      onContinueHandlerForPaidForms(setVerificationsLeft);
    } else {
      onContinueHandlerForUnpaidForms(setVerificationsLeft);
    }
  };

  const onContinueBtnClick = async (formType: 'paid' | 'unpaid') => {
    if (txnInfo?.state === TxnState.NOT_INITIALIZED && txnInfo?.questionnaire?.isRequired) {
      setOpenStartingProcessDialog({
        callback: async () => {
          onContinueFnBasedOnFormType(formType);
          return;
        }
      });
    } else {
      onContinueFnBasedOnFormType(formType);
    }
  };

  const handlePayButton = async () => {
    setContinueToPay(true);
    await onPayBtnClick();
    setContinueToPay(false);
  };

  return (
    <>
      {formData && (
        <>
          {remainingVerificationsCount <= 0 &&
            [TxnState.IN_PROGRESS, TxnState.COMPLETED].includes(txnInfo.state) && (
              <Grid
                container
                direction="column"
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                sx={{ ...styles.bottomStickyWrapper }}>
                {isPaymentComplete() && (
                  <Alert severity="info" sx={styles.paymentCompleteAlert}>
                    Congratulations! Your payment is complete. Your verifications are under process.
                    {willExpressVerificationETABeAffected() &&
                      ' ETA of verification will be affected as there are non-express verifications currently under process. '}
                  </Alert>
                )}
                {showPayBtn() && (
                  <LoadingButton
                    loading={continueToPay}
                    variant="contained"
                    // color="primary"
                    id={txnInfo?.paymentRequired ? 'Pay' : 'Done'}
                    onClick={handlePayButton}>
                    {txnInfo?.paymentRequired
                      ? `${txnInfo?.discount ? `Pay (${txnInfo?.discount?.percentage}% Additional Discount Applied)` : 'Continue to Pay'}`
                      : 'Done'}
                  </LoadingButton>
                )}
              </Grid>
            )}

          {txnInfo?.userPaymentRequired ? (
            !(
              remainingVerificationsCount <= 0 &&
              [TxnState.IN_PROGRESS, TxnState.COMPLETED, TxnState.CLOSED].includes(txnInfo.state)
            ) && (
              <>
                <ContinueBtn onContinueBtnClick={onContinueBtnClick} type={'paid'} />
              </>
            )
          ) : remainingVerificationsCount > 0 ? (
            <ContinueBtn onContinueBtnClick={onContinueBtnClick} type={'unpaid'} />
          ) : null}
        </>
      )}

      {/* verifications pending - required in form | min in a category (eg. 1 out of n) */}
      <PendingVerifications
        verificationsLeft={verificationsLeft}
        setVerificationsLeft={setVerificationsLeft}
      />
      {showExpressVerificationWarning && (
        <ExpressVerificationWarning
          open={showExpressVerificationWarning}
          setOpen={setShowExpressVerificationWarning}
          onProceed={() => {
            if (isExpressOpted) {
              return navigateTo('/select-mode');
            }
            navigateTo('/choose-payment-region');
          }}
        />
      )}
    </>
  );
}

export default FormBottomRow;
